import { EventEmitter, Injectable } from '@angular/core';
import { ColumnConfig, Filter, Order, OrderType, Paginator, Search, SessionCookieService, TableService, UtilService } from '@docpath/linces-angular-components';
import { Table, TableField, TableFieldDate, TableFieldIcon, TableFieldIcons, TableFieldText, TableRow } from '@docpath/webcomponents';
import { faCheck, faWarning, faXmark } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { Constants } from 'src/app/constants/constants';
import { CloudService } from 'src/app/shared/services/cloud.service';

import { Job, JobStatus } from '../../models/job';
import { StatisticsHttpService } from '../../services/statistics-http.service';

@Injectable({
    providedIn: 'root'
})
export class StatisticsTableService {

    jobsLoaded: EventEmitter<Job[]> = new EventEmitter();
    private jobs: Job[] = [];
    private currentPage: number;
    private elements: number;
    private currentSize: number;
    private readonly i18nPrefix = 'statistics';

    // eslint-disable-next-line max-params
    constructor(
        private readonly statisticsHttpService: StatisticsHttpService,
        private readonly utilService: UtilService,
        private readonly tableService: TableService,
        private readonly sessionCookieService: SessionCookieService,
        private readonly cloudService: CloudService,
        private readonly translateService: TranslateService
    ) {
        this.currentPage = 1;
        this.elements = 0;
        this.currentSize = 25;
    }

    getJobs(): Job[] {
        return this.jobs;
    }
    getCurrentPage(): number {
        return this.currentPage;
    }
    getElements(): number {
        return this.elements;
    }
    getCurrentSize(): number {
        return this.currentSize;
    }
    getSelectedJob(table: Table): Job[] {
        return table.body.filter(row => row.selected).map(row => row.content);
    }

    // eslint-disable-next-line max-params, sonarjs/sonar-max-params
    loadJobs(loading: boolean, dgeId: number, numberHours: number, customNumberHoursFrom: string, customNumberHoursTo: string, lastJobId: number, filters: Filter[], page = this.currentPage, size = this.currentSize): void {
        this.currentPage = page;
        this.currentSize = size;

        const search = new Search(filters, [new Order(Constants.STATISTICS_JOB_IDJOB, OrderType.Desc)], [],
            new Paginator(page - 1, this.currentSize, this.elements));

        this.statisticsHttpService.getJobs(loading, dgeId, numberHours, customNumberHoursFrom, customNumberHoursTo, lastJobId, search, (result, totalSize) => {
            this.elements = totalSize;
            this.jobs = result;
            this.jobsLoaded.emit(result);
        });
    }

    initTable(): Table {
        const header = this.initTableHeader();
        const body = this.initTableBody([]);
        return new Table(header, body);
    }

    initTableHeader(): TableRow {
        const style = 'text-align:center';

        const tableRow = new TableRow([]);

        tableRow.fields.push(new TableFieldText(`${this.i18nPrefix}.table.id`, undefined, undefined, `${this.i18nPrefix}.table.id`, '8%', true));
        if (!(this.cloudService.isModeCloud() && this.sessionCookieService.getUserLogged()?.role === Constants.ROLE_NORMAL)) {
            tableRow.fields.push(new TableFieldText(`${this.i18nPrefix}.table.dge`, undefined, undefined, `${this.i18nPrefix}.table.dge`, '6%', true));
        }
        tableRow.fields.push(new TableFieldText(
            this.cloudService.isModeCloud() ? `${this.i18nPrefix}.table.transactions` : `${this.i18nPrefix}.table.documents`,
            undefined,
            { style },
            this.cloudService.isModeCloud() ? `${this.i18nPrefix}.table.transactions` : `${this.i18nPrefix}.table.documents`,
            '5%',
            true
        ));
        tableRow.fields.push(new TableFieldText(`${this.i18nPrefix}.table.pages`, undefined, { style }, `${this.i18nPrefix}.table.pages`, '5%', true));
        tableRow.fields.push(new TableFieldText(`${this.i18nPrefix}.table.form-name`, undefined, undefined, `${this.i18nPrefix}.table.form-name`, '9%', true));
        tableRow.fields.push(new TableFieldText(`${this.i18nPrefix}.table.started`, undefined, { style }, `${this.i18nPrefix}.table.started`, '10%', true));
        tableRow.fields.push(new TableFieldText(`${this.i18nPrefix}.table.finished`, undefined, { style }, `${this.i18nPrefix}.table.finished`, '10%', true));
        tableRow.fields.push(new TableFieldText(`${this.i18nPrefix}.table.queue-time`, undefined, { style }, `${this.i18nPrefix}.table.queue-time`, '5%', true));
        tableRow.fields.push(new TableFieldText(`${this.i18nPrefix}.table.execution-time`, undefined, { style }, `${this.i18nPrefix}.table.execution-time`, '5%', true));
        tableRow.fields.push(new TableFieldText(`${this.i18nPrefix}.table.tag1`, undefined, { style }, `${this.i18nPrefix}.table.tag1`, '5%', true));
        tableRow.fields.push(new TableFieldText(`${this.i18nPrefix}.table.tag2`, undefined, { style }, `${this.i18nPrefix}.table.tag2`, '5%', true));
        tableRow.fields.push(new TableFieldText(`${this.i18nPrefix}.table.tag3`, undefined, { style }, `${this.i18nPrefix}.table.tag3`, '5%', true));
        tableRow.fields.push(new TableFieldText(`${this.i18nPrefix}.table.tag4`, undefined, { style }, `${this.i18nPrefix}.table.tag4`, '5%', true));
        tableRow.fields.push(new TableFieldText(`${this.i18nPrefix}.table.tag5`, undefined, { style }, `${this.i18nPrefix}.table.tag5`, '5%', true));
        tableRow.fields.push(new TableFieldText(`${this.i18nPrefix}.table.preview.label`, undefined, { style }, `${this.i18nPrefix}.table.preview.label`, '5%', true));
        tableRow.fields.push(new TableFieldText(`${this.i18nPrefix}.table.status.label`, undefined, { style }, `${this.i18nPrefix}.table.status.label`, '7%', true));

        return tableRow;
    }

    initTableBody(job: Job[]): TableRow[] {
        const style = 'text-align:center';

        return job.map(job => {
            const fields: TableField[] = [],
                { idJob, dge, documents, pages, formName, started, finished, queueTime, executionTime, tag1, tag2, tag3, tag4, tag5, status, transactions, preview } = job;

            fields.push(new TableFieldText(idJob.toString(), undefined, { style: 'font-weight: bold' }, undefined, '8%'));
            
            if (!(this.cloudService.isModeCloud() && this.sessionCookieService.getUserLogged()?.role === Constants.ROLE_NORMAL)) {
                fields.push(new TableFieldText(dge, undefined, undefined, dge, '6%'));
            }
            if (this.cloudService.isModeCloud()) {
                fields.push(new TableFieldText(transactions.toString(), undefined, { style }, undefined, '5%'));
            } else {
                fields.push(new TableFieldText(documents.toString(), undefined, { style }, undefined, '5%'));
            }
            
            fields.push(new TableFieldText(pages.toString(), undefined, { style }, undefined, '5%'));
            fields.push(new TableFieldText(formName, undefined, undefined, formName, '9%'));
            fields.push(new TableFieldDate(started, this.utilService.getDateFormats(), undefined, undefined, { style }, '10%', undefined));
            fields.push(new TableFieldDate(finished, this.utilService.getDateFormats(), undefined, undefined, { style }, '10%', undefined));
            fields.push(new TableFieldText(`${queueTime.toString()} ms`, undefined, { style }, undefined, '5%'));
            fields.push(new TableFieldText(`${executionTime.toString()} ms`, undefined, { style }, undefined, '5%'));
            fields.push(new TableFieldText(tag1, undefined, { style }, tag1, '5%'));
            fields.push(new TableFieldText(tag2, undefined, { style }, tag2, '5%'));
            fields.push(new TableFieldText(tag3, undefined, { style }, tag3, '5%'));
            fields.push(new TableFieldText(tag4, undefined, { style }, tag4, '5%'));
            fields.push(new TableFieldText(tag5, undefined, { style }, tag5, '5%'));            
            fields.push(new TableFieldText(this.translateService.instant(`${this.i18nPrefix}.table.preview.option.${preview}`), undefined, { style }, this.translateService.instant(`${this.i18nPrefix}.table.preview.option.${preview}`), '5%'));
            fields.push(this.getStatusIcons(status)); 

            const tableRow = new TableRow(fields);
            tableRow.content = job;
            return tableRow;
        });
    }

    getStatusIcons(status: string): TableFieldIcons {
        let fieldIcons: TableFieldIcons;
        const icons: TableFieldIcon[] = [];
        const style = 'text-align:center; width: 7%;';

        if (status === JobStatus.OK) {
            icons.push(new TableFieldIcon(faCheck, undefined, undefined, undefined, undefined, { cssClass: 'text-dpgreen' }));
            fieldIcons = new TableFieldIcons(icons, { style });
        } else if (status === JobStatus.KO) {
            icons.push(new TableFieldIcon(faXmark, undefined, undefined, undefined, undefined, { cssClass: 'text-dpred' }));
            fieldIcons = new TableFieldIcons(icons, { style });
        } else {
            icons.push(new TableFieldIcon(faWarning, undefined, undefined, undefined, undefined, { cssClass: 'text-dpyellow' }));
            fieldIcons = new TableFieldIcons(icons, { style });
        }
        return fieldIcons;
    }

    refreshColumns(table: Table): void {
        const columnConfig = this.tableService.getColumns(this.i18nPrefix);

        columnConfig.forEach((column: ColumnConfig, index: number) => {
            const field = table.header.fields[index] as TableFieldText;

            field.visible = column.visible;
            field.fieldWidth = `${column.currentWidth}%`;

            table.body.forEach((row: TableRow) => {
                const field = row.fields[index] as TableFieldText;

                field.visible = column.visible;
                field.fieldWidth = `${column.currentWidth}%`;
            });
        });
    }

    getDefaultColumns(): ColumnConfig[] {

        const columns = [];

        columns.push(new ColumnConfig({ name: Constants.STATISTICS_JOB_IDJOB, visible: true, currentWidth: 5, minWidth: 5, maxWidth: 5 }));
        if (!(this.cloudService.isModeCloud() && this.sessionCookieService.getUserLogged()?.role === Constants.ROLE_NORMAL)) {
            columns.push(new ColumnConfig({ name: Constants.STATISTICS_JOB_DGE, visible: true, currentWidth: 10, minWidth: 10, maxWidth: 10 }));
        }
        if (this.cloudService.isModeCloud()) {
            columns.push(new ColumnConfig({ name: Constants.STATISTICS_JOB_TRANSACTIONS, visible: true, currentWidth: 5, minWidth: 5, maxWidth: 5 }));
        } else {
            columns.push(new ColumnConfig({ name: Constants.STATISTICS_JOB_DOCUMENTS, visible: true, currentWidth: 5, minWidth: 5, maxWidth: 5 }));
        }        
        columns.push(new ColumnConfig({ name: Constants.STATISTICS_JOB_PAGES, visible: true, currentWidth: 5, minWidth: 5, maxWidth: 5 }));
        columns.push(new ColumnConfig({ name: Constants.STATISTICS_JOB_FORM_NAME, visible: true, currentWidth: 10, minWidth: 10, maxWidth: 10 }));
        columns.push(new ColumnConfig({ name: Constants.STATISTICS_JOB_STARTED, visible: true, currentWidth: 10, minWidth: 10, maxWidth: 10 }));
        columns.push(new ColumnConfig({ name: Constants.STATISTICS_JOB_FINISHED, visible: true, currentWidth: 10, minWidth: 10, maxWidth: 10 }));
        columns.push(new ColumnConfig({ name: Constants.STATISTICS_JOB_QUEUE_TIME, visible: true, currentWidth: 5, minWidth: 5, maxWidth: 5 }));
        columns.push(new ColumnConfig({ name: Constants.STATISTICS_JOB_EXECUTION_TIME, visible: true, currentWidth: 5, minWidth: 5, maxWidth: 5 }));
        columns.push(new ColumnConfig({ name: Constants.STATISTICS_JOB_TAG1, visible: false, currentWidth: 5, minWidth: 5, maxWidth: 5 }));
        columns.push(new ColumnConfig({ name: Constants.STATISTICS_JOB_TAG2, visible: false, currentWidth: 5, minWidth: 5, maxWidth: 5 }));
        columns.push(new ColumnConfig({ name: Constants.STATISTICS_JOB_TAG3, visible: false, currentWidth: 5, minWidth: 5, maxWidth: 5 }));
        columns.push(new ColumnConfig({ name: Constants.STATISTICS_JOB_TAG4, visible: false, currentWidth: 5, minWidth: 5, maxWidth: 5 }));
        columns.push(new ColumnConfig({ name: Constants.STATISTICS_JOB_TAG5, visible: false, currentWidth: 5, minWidth: 5, maxWidth: 5 }));
        columns.push(new ColumnConfig({ name: Constants.STATISTICS_JOB_PREVIEW, visible: true, currentWidth: 5, minWidth: 5, maxWidth: 5 }));
        columns.push(new ColumnConfig({ name: Constants.STATISTICS_JOB_STATUS, visible: true, currentWidth: 5, minWidth: 5, maxWidth: 5 }));

        return columns;
    }
}